import { HistoryOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  type IAdminProduct,
  type ILanguageType,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import {
  fixString,
  type IErrorMessage,
  type IFixResponse,
  type ILintStringResponse,
  lintString,
} from '@mahawi/markdown-linter';
import { type RootState } from '@reducers';
import {
  Button,
  Col,
  Divider,
  Form,
  type FormInstance,
  Input,
  Popover,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import Markdown from 'markdown-to-jsx';
import React, { type CSSProperties, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { type Dispatch } from 'redux';
import slugify from 'slugify';

import { createMdLinterConfig } from '@/markdown';

import { normalizeWarehouseProductName } from '../../fragments/normalize-warehouse-product-name';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { EHotkey, useHotkey } from '../../hooks/use-hotkey';
import {
  copyDescriptions,
  descriptionLive,
  nameLive,
  namesDescriptionsSlugsUpdate,
  productAIEnhanceDescription,
  productAIEnhanceDescriptionAllLanguages,
  productAITranslateNameAllLanguages,
  slugLive,
} from '../../reducers/product/reducer';
import { SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';
import FormSubmitFooter from '../form-submit-footer';
import GoogleTranslate from '../google-translate';
import GoogleTranslateAutomat, {
  type ITextTranslate,
} from '../google-translate-automat';
import StatusIcon from '../status-icon';
import DescriptionHistoryDrawer from './description-history-drawer';
import CopyDescriptionsForm, {
  type IDescriptionProduct,
} from './language-copy-form';
import LintTable, { type LintDataType } from './language-lint-table';
import LanguageMetaSection from './language-meta-section';

interface IForm {
  name: string;
  description: string;
  slug: string;
  metaDescription: string;
  metaKeywords: string[];
}

const styleMarkdownLivePreview: CSSProperties = {
  minHeight: '50vh',
  border: '.1rem solid rgba(0, 0, 0, 0.15)',
  borderRadius: '6px',
};

const styleMarkdownEditor: CSSProperties = {
  minHeight: '50vh',
};

// ⚠️ Google Translate clipboard/focus workflow — DO NOT MODIFY
const translateName = (
  languageCode: string,
  form: FormInstance,
  dispatch: Dispatch,
): void => {
  window.addEventListener(
    'focus',
    async (): Promise<void> => {
      const text: string = await navigator.clipboard.readText();
      const tt: string = text.trim();

      dispatch(nameLive(tt, languageCode));
      dispatch(
        slugLive(
          slugify(text.trim(), { lower: true, strict: true }),
          languageCode,
        ),
      );

      form.submit();
    },
    { once: true },
  );
};

// ⚠️ Google Translate clipboard/focus workflow — DO NOT MODIFY
const translateDescription = (
  languageCode: string,
  form: FormInstance,
  dispatch: Dispatch,
): void => {
  window.addEventListener(
    'focus',
    async (): Promise<void> => {
      const text: string = await navigator.clipboard.readText();
      const tt: string = text.trim();

      dispatch(descriptionLive(tt, languageCode));

      form.submit();
    },
    { once: true },
  );
};

const PRODUCT_LANGUAGE_HOTKEYS: EHotkey[] = [
  EHotkey.PASTE_NAME,
  EHotkey.PASTE_DESCRIPTION,
  EHotkey.SUBMIT_FORM,
];

const hotkeyHandler = async (
  hotkey: EHotkey,
  form: FormInstance,
  languageCode: string,
  dispatch: Dispatch,
): Promise<void> => {
  if (hotkey === EHotkey.SUBMIT_FORM) {
    form.submit();

    return;
  }

  const text: string = await navigator.clipboard.readText();

  if (hotkey === EHotkey.PASTE_NAME) {
    dispatch(nameLive(text.trim(), languageCode));
    dispatch(
      slugLive(
        slugify(text.trim(), { lower: true, strict: true }),
        languageCode,
      ),
    );
  } else if (hotkey === EHotkey.PASTE_DESCRIPTION) {
    dispatch(descriptionLive(text.trim(), languageCode));
  }
};

const onFinish = (
  { name, description, slug, metaDescription, metaKeywords }: IForm,
  productUUID: string,
  languageCode: string,
  dispatch: Dispatch,
): void => {
  const names = [
    { code: languageCode, value: normalizeWarehouseProductName(name) },
  ];
  const descriptions = [{ code: languageCode, value: description.trim() }];
  const slugs = [{ code: languageCode, value: slug.trim() }];
  const metaDescriptions = [
    { code: languageCode, value: (metaDescription ?? '').trim() },
  ];
  const metaKw = [{ code: languageCode, value: metaKeywords ?? [] }];

  dispatch(
    namesDescriptionsSlugsUpdate(
      productUUID,
      names,
      descriptions,
      slugs,
      metaDescriptions,
      metaKw,
    ),
  );
};

const infoContent: React.ReactElement = (
  <div>
    <div>
      <Typography.Text strong>option + w</Typography.Text> - insert into name
    </div>
    <div>
      <Typography.Text strong>option + e</Typography.Text> - insert into
      description
    </div>
    <div>
      <Typography.Text strong>option + d</Typography.Text> - submit form
    </div>
  </div>
);

function ProductLanguage({
  setLanguageTypeCodeError,
}: {
  setLanguageTypeCodeError: (languageTypeCodeError: string[]) => void;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const productUUID = useSelector((s: RootState) => s.Product.uuid);
  const productInProcess = useSelector((s: RootState) => s.Product.inProcess);
  const productUpdatedAt = useSelector((s: RootState) => s.Product.updatedAt);
  const productDescriptions = useSelector(
    (s: RootState) => s.Product.descriptions,
  );
  const productNames = useSelector((s: RootState) => s.Product.names);
  const productSlugs = useSelector((s: RootState) => s.Product.slugs);
  const productMetaDescriptions = useSelector(
    (s: RootState) => s.Product.metaDescriptions,
  );
  const productMetaKeywords = useSelector(
    (s: RootState) => s.Product.metaKeywords,
  );
  const productBrandName = useSelector((s: RootState) => s.Product.brand?.name);
  const productsAll = useSelector((s: RootState) => s.Products.products);
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const languages = useSelector((s: RootState) => s.Language.languages);

  const [dataSource, setDataSource] = useState<LintDataType[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [form] = Form.useForm<IForm>();
  const [productDescription, setProductDescription] = useState<string>('');
  const [productDescriptionEN, setProductDescriptionEN] =
    useState<ITranslation>();
  const [productName, setProductName] = useState<string>('');
  const [productNameEN, setProductNameEN] = useState<ITranslation>();
  const [products, setProducts] = useState<IAdminProduct[]>([]);
  const [wasCopied, setCopied] = useState(false);
  const [invalidLanguageTranslations, setInvalidLanguageTranslations] =
    useState<Set<string>>(new Set());
  const [copyDescriptionProducts, setCopyDescriptionProducts] = useState<
    IDescriptionProduct[]
  >([]);

  useEffect((): void => {
    setCopied(false);
  }, [languageType?.code]);

  useHotkey(PRODUCT_LANGUAGE_HOTKEYS, (hotkey: EHotkey): void => {
    if (languageType) {
      hotkeyHandler(hotkey, form, languageType.code, dispatch);
    }
  });

  const productDescriptionsJson: string = JSON.stringify(productDescriptions);
  const productNamesJson: string = JSON.stringify(productNames);
  const productSlugsJson: string = JSON.stringify(productSlugs);

  useEffect((): void => {
    const descriptionENUE: ITranslation | undefined = productDescriptions.find(
      (p: ITranslation): boolean => p.code === 'EN',
    );
    setProductDescriptionEN(descriptionENUE);
  }, [productDescriptions, productDescriptionsJson]);

  useEffect((): void => {
    const invalidLanguageTranslationsUE = new Set<string>();

    languages?.forEach((language: ILanguageType): void => {
      const pn: ITranslation | undefined = productNames.find(
        (n: ITranslation): boolean => n.code === language.code,
      );
      if (!pn || pn.value.trim().length <= 2) {
        invalidLanguageTranslationsUE.add(language.code);
      }

      const ps: ITranslation | undefined = productSlugs.find(
        (s: ITranslation): boolean => s.code === language.code,
      );
      if (!ps || ps.value.trim().length <= 2) {
        invalidLanguageTranslationsUE.add(language.code);
      }

      const pd: ITranslation | undefined = productDescriptions.find(
        (d: ITranslation): boolean => d.code === language.code,
      );
      if (!pd || pd.value.trim().length <= 2) {
        invalidLanguageTranslationsUE.add(language.code);
      }
    });

    setInvalidLanguageTranslations(invalidLanguageTranslationsUE);
  }, [
    languages,
    productDescriptions,
    productNames,
    productSlugs,
    productDescriptionsJson,
    productNamesJson,
    productSlugsJson,
  ]);

  useEffect((): void => {
    const dataSourceUE: LintDataType[] = [];
    const invalidLanguageTranslationsUE = new Set<string>();
    let currentDescription = '';

    const config = createMdLinterConfig(productBrandName);

    productDescriptions.forEach((description: ITranslation): void => {
      let fixedContentLoop: string = description.value;

      const fixed: IFixResponse = fixString(fixedContentLoop, config);
      fixedContentLoop = fixed.content;

      const lintFixed: ILintStringResponse = lintString(
        fixedContentLoop,
        config,
      );

      [...lintFixed.errors].forEach(
        (error: IErrorMessage, ind: number): void => {
          dataSourceUE.push({
            key: `${description.code}-${ind}`,
            languageTypeCode: description.code,
            lineNumber: error.lineNumber,
            ruleName: error.ruleName,
            ruleDescription: error.ruleDescription,
            line: error.line,
            detail: error.detail,
          });

          invalidLanguageTranslationsUE.add(description.code);
        },
      );

      if (description.code === languageType?.code) {
        const lastFixed: IFixResponse = fixString(fixedContentLoop, config);
        currentDescription = lastFixed.content;
      }
    });

    setDataSource(dataSourceUE);
    setProductDescription(currentDescription);
    form.setFieldsValue({ description: currentDescription });
    setInvalidLanguageTranslations(invalidLanguageTranslationsUE);
  }, [
    form,
    languageType?.code,
    productBrandName,
    productDescriptions,
    productDescriptionsJson,
  ]);

  useEffect((): void => {
    const productNameENUE: ITranslation | undefined = productNames.find(
      (p: ITranslation): boolean => p.code === 'EN',
    );
    setProductNameEN(productNameENUE);

    const productNameUE: string | null = getTranslation(
      productNames,
      languageType,
    );

    const productNameLocalized: string = productNameUE || '';
    setProductName(productNameLocalized);

    const productSlugLocalizedUE: string = slugify(productNameLocalized, {
      lower: true,
      strict: true,
    });

    form.setFieldsValue({
      name: productNameLocalized,
      slug: productSlugLocalizedUE,
    });

    if (!languageType) {
      return;
    }

    dispatch(slugLive(productSlugLocalizedUE.trim(), languageType.code));
  }, [form, languageType?.code, productNames, productNamesJson, dispatch]);

  const productMetaDescriptionsJson: string = JSON.stringify(
    productMetaDescriptions,
  );
  const productMetaKeywordsJson: string = JSON.stringify(productMetaKeywords);

  useEffect((): void => {
    if (!languageType) {
      return;
    }

    const metaDesc = productMetaDescriptions?.find(
      (t: ITranslation): boolean => t.code === languageType!.code,
    );
    const metaKw = productMetaKeywords?.find(
      ({ code }) => code === languageType!.code,
    );

    form.setFieldsValue({
      metaDescription: metaDesc?.value ?? '',
      metaKeywords: metaKw?.value ?? [],
    });
  }, [
    form,
    languageType?.code,
    productMetaDescriptions,
    productMetaKeywords,
    productMetaDescriptionsJson,
    productMetaKeywordsJson,
  ]);

  const productsJson: string = JSON.stringify(productsAll);

  useEffect((): void => {
    const productsUE: IAdminProduct[] =
      productsAll?.filter(
        ({ uuid }: IAdminProduct): boolean => uuid !== productUUID,
      ) || [];

    setProducts(productsUE);
  }, [productsJson, productsAll, productUUID]);

  useEffect((): void => {
    const selectOptionsSourceProducts = products.map((product) => {
      const pn: string | null = getTranslation(product.names, languageType);

      const label = `${pn || '****'} → ${product.uuid}`;

      return {
        value: product.uuid,
        label,
      };
    });

    setCopyDescriptionProducts(selectOptionsSourceProducts);
  }, [products, languageType?.code]);

  useEffect((): void => {
    setLanguageTypeCodeError([...invalidLanguageTranslations]);
  }, [invalidLanguageTranslations, setLanguageTypeCodeError]);

  const gtLinkName: string = `GT name EN → ${languageType?.code}`;
  const gtLinkNameReversed: string = `GT name ${languageType?.code} → EN`;
  const gtLinkDescription: string = `GT description EN → ${languageType?.code}`;
  const gtLinkDescriptionReversed: string = `GT description ${languageType?.code} → EN`;

  const copyButtonText: string = wasCopied
    ? `${languageType?.code} description is in clipboard`
    : `Copy ${languageType?.code} description to clipboard`;

  const productNameTranslated: string = productNameEN?.value || '';
  const productDescriptionTranslated: string =
    productDescriptionEN?.value || '';

  const texts: ITextTranslate[] = [
    {
      text: productNameTranslated,
      callbacks: [
        (text: string, lc: string): void => {
          form.setFieldsValue({
            name: text,
            slug: slugify(text, { lower: true, strict: true }),
          });

          dispatch(nameLive(text, lc));

          dispatch(slugLive(slugify(text, { lower: true, strict: true }), lc));
        },
      ],
    },
    {
      text: productDescriptionTranslated,
      callbacks: [
        (text: string, lc: string): void => {
          form.setFieldValue('description', text);

          dispatch(descriptionLive(text, lc));
        },
      ],
    },
  ];

  return (
    <>
      <Space size={SPACING.lg}>
        <Popover title="Shortcuts" trigger="hover" content={infoContent}>
          <InfoCircleOutlined />
        </Popover>

        <StatusIcon
          ok={
            [...invalidLanguageTranslations].filter(
              (code: string): boolean => code === languageType?.code,
            ).length === 0
          }
        />
      </Space>
      <CopyDescriptionsForm
        copyDescriptionProducts={copyDescriptionProducts}
        onSubmit={(productSourceUUID: string): void => {
          dispatch(copyDescriptions(productSourceUUID, productUUID));
        }}
        inProcess={productInProcess}
      />

      <Form
        form={form}
        onFinish={(values: IForm): void => {
          if (!languageType) {
            return;
          }

          onFinish(values, productUUID, languageType.code, dispatch);
        }}
        layout="vertical"
      >
        <Space size={SPACING.lg} wrap align="center">
          <FormSubmitFooter
            inProcess={productInProcess}
            updatedAt={productUpdatedAt}
          />

          <Button
            size="small"
            onClick={(): void => {
              if (!languageType) {
                return;
              }

              dispatch(
                productAIEnhanceDescription(productUUID, languageType.code),
              );
            }}
            disabled={productInProcess}
          >
            Enhance description (AI)
          </Button>

          <Tooltip title="Enhance description using AI and translate to all active languages">
            <Button
              size="small"
              onClick={(): void => {
                if (!languageType || !productUUID) {
                  return;
                }

                dispatch(
                  productAIEnhanceDescriptionAllLanguages(
                    productUUID,
                    languageType.code,
                  ),
                );
              }}
              disabled={productInProcess || !productUUID || !languageType}
            >
              Enhance description – all languages (AI)
            </Button>
          </Tooltip>

          <Tooltip title="Description history">
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={(): void => setHistoryOpen(true)}
            />
          </Tooltip>

          <CopyToClipboard
            text={productDescription}
            title={copyButtonText}
            size="small"
            type="primary"
          >
            {copyButtonText}
          </CopyToClipboard>

          <GoogleTranslateAutomat
            texts={texts}
            submit={form.submit}
            isSaving={productInProcess}
            size="small"
          />

          <GoogleTranslate
            fromLanguageCode="EN"
            toLanguageCode={languageType?.code}
            name={gtLinkName}
            text={productNameTranslated}
            onClick={(): void => {
              if (!languageType) {
                return;
              }

              translateName(languageType.code, form, dispatch);
            }}
          />

          <GoogleTranslate
            fromLanguageCode="EN"
            toLanguageCode={languageType?.code}
            name={gtLinkDescription}
            text={productDescriptionTranslated}
            onClick={(): void => {
              if (!languageType) {
                return;
              }

              translateDescription(languageType.code, form, dispatch);
            }}
          />

          <GoogleTranslate
            fromLanguageCode={languageType?.code}
            toLanguageCode="EN"
            name={gtLinkNameReversed}
            text={productName}
            onClick={(): void => translateName('EN', form, dispatch)}
          />

          <GoogleTranslate
            fromLanguageCode={languageType?.code}
            toLanguageCode="EN"
            name={gtLinkDescriptionReversed}
            text={productDescription}
            onClick={(): void => translateDescription('EN', form, dispatch)}
          />
        </Space>

        <Divider />

        <Row>
          <Col xs={24} sm={12}>
            <Form.Item
              name="name"
              rules={[
                {
                  required: true,
                },
                {
                  type: 'string',
                  min: 2,
                },
              ]}
              label="Name"
            >
              <Input
                placeholder="Name"
                onBlur={(e): void => {
                  if (!languageType) {
                    return;
                  }

                  dispatch(nameLive(e.target.value.trim(), languageType.code));
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col>
            <Tooltip title="Translate product name to all active languages using AI">
              <Button
                size="small"
                disabled={productInProcess || !productUUID || !languageType}
                onClick={(): void => {
                  if (!languageType || !productUUID) {
                    return;
                  }

                  dispatch(
                    productAITranslateNameAllLanguages(
                      productUUID,
                      languageType.code,
                    ),
                  );
                }}
              >
                Translate name – all languages (AI)
              </Button>
            </Tooltip>
          </Col>
        </Row>

        <Row>
          <Col xs={24} sm={12}>
            <Form.Item
              name="slug"
              rules={[
                {
                  required: true,
                },
                {
                  type: 'string',
                  min: 2,
                },
              ]}
              label="Slug"
            >
              <Input
                placeholder="Slug"
                onBlur={(e): void => {
                  if (!languageType) {
                    return;
                  }

                  dispatch(slugLive(e.target.value.trim(), languageType.code));
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={SPACING.lg}>
          <Col xs={24} sm={12} className="gutter-row">
            <Form.Item
              name="description"
              rules={[
                {
                  required: true,
                },
                {
                  type: 'string',
                  min: 2,
                },
              ]}
              label="Description"
            >
              <Input.TextArea
                style={styleMarkdownEditor}
                rows={4}
                placeholder="Description"
                onBlur={(e) => {
                  if (!languageType) {
                    return;
                  }

                  dispatch(
                    descriptionLive(e.target.value.trim(), languageType.code),
                  );
                }}
              />
            </Form.Item>
          </Col>
          <Col
            span={12}
            className="gutter-row"
            style={styleMarkdownLivePreview}
          >
            <Markdown>{productDescription}</Markdown>
          </Col>
        </Row>

        <Row>
          <Col span={24} className="gutter-row">
            <span>Description has {productDescription.length} characters.</span>
          </Col>

          <Col span={24} className="gutter-row">
            <LintTable dataSource={dataSource} />
          </Col>
        </Row>

        <LanguageMetaSection
          languageTypeCode={languageType?.code}
          productUUID={productUUID}
          inProcess={productInProcess}
          dispatch={dispatch}
        />
      </Form>

      <DescriptionHistoryDrawer
        open={historyOpen}
        onClose={(): void => setHistoryOpen(false)}
        productUUID={productUUID}
        languageCode={languageType?.code ?? ''}
        currentDescriptions={productDescriptions}
      />
    </>
  );
}

export default ProductLanguage;
